import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Mail,
  Sparkles,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Send,
  Loader2,
  KeyRound,
  GraduationCap,
  Shield,
  Smartphone,
} from "lucide-react";
import { authApi } from "../lib/authApi";
import { showError, showSuccess } from "../lib/toast";

interface FormState {
  email: string;
  code: string[];
}

interface FieldError {
  email?: string;
  code?: string;
  general?: string;
}

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<FormState>({
    email: "",
    code: ["", "", "", "", "", ""],
  });
  const [errors, setErrors] = useState<FieldError>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resent, setResent] = useState(false);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [verifying, setVerifying] = useState(false);
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const email = searchParams.get("email");
    if (email) {
      setForm((prev) => ({ ...prev, email }));
      setCurrentStep(2);
    }
  }, [searchParams]);

  // Auto-focus first input when step 2 loads
  useEffect(() => {
    if (currentStep === 2 && codeInputRefs.current[0]) {
      codeInputRefs.current[0].focus();
    }
  }, [currentStep]);

  // Auto-verify when all 6 digits are entered
  useEffect(() => {
    const fullCode = form.code.join("");
    if (fullCode.length === 6 && !loading && !verifying && currentStep === 2) {
      handleVerify(fullCode);
    }
  }, [form.code]);

  const validateEmail = (): boolean => {
    if (!form.email.trim()) {
      setErrors({ email: "Email is required." });
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      setErrors({ email: "Enter a valid email address." });
      return false;
    }
    setErrors({});
    return true;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail()) return;
    setCurrentStep(2);
  };

  const handleVerify = async (verificationCode: string) => {
    setVerifying(true);
    setLoading(true);
    setErrors({});

    try {
      await authApi.verifyEmail(form.email, verificationCode);
      setSuccess(true);
      showSuccess("Email verified successfully!");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err: unknown) {
      const error = err as Error;
      showError(error.message || "Invalid verification code.");
      setErrors({ code: error.message || "Invalid verification code" });
      setForm({ ...form, code: ["", "", "", "", "", ""] });
      codeInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!form.email.trim()) {
      showError("Enter your email first.");
      return;
    }
    setLoading(true);
    try {
      await authApi.resendVerification(form.email);
      setResent(true);
      showSuccess("Verification code sent!");
      setForm({ ...form, code: ["", "", "", "", "", ""] });
      codeInputRefs.current[0]?.focus();
      setTimeout(() => setResent(false), 3000);
    } catch (err: unknown) {
      const error = err as Error;
      showError(error.message || "Failed to resend code.");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) return;
    if (!/^\d*$/.test(value)) return;

    const newCode = [...form.code];
    newCode[index] = value;
    setForm({ ...form, code: newCode });
    setErrors({});

    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !form.code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (pastedData) {
      const newCode = [...form.code];
      for (let i = 0; i < pastedData.length; i++) {
        newCode[i] = pastedData[i];
      }
      setForm({ ...form, code: newCode });
      codeInputRefs.current[Math.min(pastedData.length - 1, 5)]?.focus();
    }
  };

  const codeString = form.code.join("");
  const isCodeComplete = codeString.length === 6;

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-xl shadow-lg p-5 border border-gray-100 animate-fade-in-up">
            <div className="inline-flex items-center justify-center w-12 h-12 mb-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-md">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-base font-bold text-gray-900 mb-1">
              Email Verified! 🎉
            </h2>
            <p className="text-[11px] text-gray-600 mb-4">
              Your email has been successfully verified.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="w-full py-1.5 text-xs font-semibold text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
            >
              Continue to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-orange-500/20 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>

      <div className="relative w-full max-w-sm">
        {/* Main Card */}
        <div className="overflow-hidden bg-white border border-gray-100 rounded-xl shadow-lg">
          {/* Header - Compact */}
          <div className="px-4 py-3 text-center bg-gradient-to-r from-orange-600 to-orange-500">
            <div className="flex items-center justify-center gap-0 font-bold text-center">
              <span className="text-white text-[1.2rem]">Learn</span>
              <span className="text-white text-[1.2rem]">Hub</span>
            </div>
            <p className="mt-1 text-[10px] text-orange-100">
              Verify Your Email
            </p>
          </div>

          {/* Content - Compact */}
          <div className="p-4">
            {currentStep === 1 ? (
              // Step 1: Email Form
              <form onSubmit={handleEmailSubmit}>
                {/* Progress Indicator - Compact */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-[10px] font-bold">
                      1
                    </div>
                    <div className="flex-1 h-0.5 bg-orange-200 rounded-full"></div>
                    <div className="w-6 h-6 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-[10px] font-bold">
                      2
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500 text-center">
                    Enter your email address
                  </p>
                </div>

                {/* Email Field */}
                <div className="mb-4">
                  <label className="block mb-1 text-[11px] font-medium text-gray-700">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => {
                        setForm({ ...form, email: e.target.value });
                        setErrors({});
                      }}
                      className={`w-full pl-9 pr-3 py-1.5 text-[11px] rounded-lg border transition-all outline-none
                        focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500
                        ${errors.email ? "border-red-300 bg-red-50" : "border-gray-200 hover:border-gray-300"}`}
                    />
                  </div>
                  {errors.email && (
                    <p className="flex items-center gap-1 mt-1 text-[10px] text-red-500">
                      <AlertCircle className="w-3 h-3" />
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full py-1.5 text-[11px] font-semibold text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
                >
                  Send Verification Code
                </button>

                {/* Login Link */}
                <p className="mt-4 text-center text-[10px] text-gray-500">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="font-semibold text-orange-500 hover:text-orange-600"
                  >
                    Sign in
                  </Link>
                </p>
              </form>
            ) : (
              // Step 2: Code Verification
              <div>
                {/* Progress Indicator - Compact */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-orange-200 text-orange-500 flex items-center justify-center text-[10px] font-bold">
                      ✓
                    </div>
                    <div className="flex-1 h-0.5 bg-orange-500 rounded-full"></div>
                    <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-[10px] font-bold">
                      2
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500 text-center">
                    Enter the verification code
                  </p>
                </div>

                {/* Email Display - Compact */}
                <div className="flex items-center justify-between p-2 mb-4 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Mail className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                    <span className="text-[11px] font-medium text-gray-700 truncate">
                      {form.email}
                    </span>
                  </div>
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="text-[10px] text-orange-500 hover:text-orange-600 font-medium ml-2 flex-shrink-0"
                  >
                    Change
                  </button>
                </div>

                {/* Error Message */}
                {errors.code && (
                  <div className="p-2 mb-3 border border-red-200 rounded-lg bg-red-50 animate-shake">
                    <p className="text-[11px] text-red-600 text-center">
                      {errors.code}
                    </p>
                  </div>
                )}

                {/* Resent Success */}
                {resent && (
                  <div className="p-2 mb-3 border border-green-200 rounded-lg bg-green-50 animate-slide-down">
                    <div className="flex items-center justify-center gap-1">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      <p className="text-[10px] text-green-600">
                        New code sent!
                      </p>
                    </div>
                  </div>
                )}

                {/* Verification in Progress */}
                {isCodeComplete && !errors.code && verifying && (
                  <div className="p-2 mb-3 border border-blue-200 rounded-lg bg-blue-50 animate-pulse">
                    <div className="flex items-center justify-center gap-1">
                      <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />
                      <p className="text-[10px] text-blue-600">Verifying...</p>
                    </div>
                  </div>
                )}

                {/* Code Input - Compact */}
                <div className="mb-4">
                  <label className="block mb-2 text-[11px] font-medium text-gray-700 text-center">
                    Verification Code
                  </label>
                  <div
                    className="flex justify-center gap-2"
                    onPaste={handlePaste}
                  >
                    {[...Array(6)].map((_, index) => (
                      <input
                        key={index}
                        ref={(el) => {
                          codeInputRefs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={form.code[index]}
                        onChange={(e) =>
                          handleCodeChange(index, e.target.value)
                        }
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        className={`w-8 h-8 text-center text-base font-bold rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500/20
                          ${
                            form.code[index]
                              ? "border-orange-500 bg-orange-50 text-orange-700"
                              : errors.code
                                ? "border-red-300 bg-red-50"
                                : "border-gray-200 bg-white hover:border-gray-300"
                          }
                          ${isCodeComplete && !errors.code && !verifying ? "border-green-500 bg-green-50" : ""}`}
                      />
                    ))}
                  </div>
                  <p className="mt-2 text-center text-[9px] text-gray-400">
                    Enter the 6-digit code sent to your email
                  </p>
                </div>

                {/* Resend Link */}
                <div className="text-center mb-4">
                  <p className="text-[10px] text-gray-500 mb-1">
                    Didn't receive the code?
                  </p>
                  <button
                    onClick={handleResend}
                    disabled={loading}
                    className="text-[10px] font-semibold text-orange-500 hover:text-orange-600 disabled:opacity-50"
                  >
                    Resend code
                  </button>
                </div>

                {/* Security Note */}
                <div className="flex items-center justify-center gap-1 pt-3 border-t border-gray-100">
                  <Shield className="w-3 h-3 text-gray-400" />
                  <p className="text-[9px] text-gray-400">
                    Code expires in 10 minutes
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="mt-3 text-center text-[9px] text-gray-400">
          Secure verification • Check spam folder
        </p>
      </div>

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out;
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default VerifyEmailPage;
